import React, { ForwardedRef, useEffect, useRef } from 'react';
import { useObjectRef } from '@react-aria/utils';
import { useRadio, AriaRadioProps } from '@react-aria/radio';
import { useSegmentedRadioProvider } from './context';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/segmentedControl/segmentedControl.module.css';
import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';

export type SegmentedRadioProps = AriaRadioProps &
  SapphireStyleProps &
  GlobalDomAttributes;

export const SegmentedRadio = React.forwardRef(function SegmentedRadio(
  props: SegmentedRadioProps,
  forwardedRef: ForwardedRef<HTMLLabelElement>
) {
  const { children, ...otherProps } = props;
  const { styleProps } = useSapphireStyleProps(otherProps);
  const ref = useObjectRef(forwardedRef);
  const inputRef = useRef<HTMLInputElement>(null);
  const { state, setActiveElement } = useSegmentedRadioProvider();
  const isActive = props.value === state.selectedValue;
  const isDisabled = props.isDisabled || state.isDisabled;
  const { inputProps } = useRadio(props, state, inputRef);

  useEffect(() => {
    if (isActive && inputRef.current) {
      setActiveElement(inputRef);
    }
  }, [isActive, inputRef.current]);

  return (
    <label
      ref={ref}
      className={clsx(
        styles['sapphire-segmented-control__button'],
        styleProps.className,
        {
          [styles['sapphire-segmented-control__button--active']]: isActive,
          [styles['sapphire-segmented-control__button--disabled']]: isDisabled,
        }
      )}
      style={styleProps.style}
    >
      <input
        {...inputProps}
        ref={inputRef}
        className={clsx(styles['sapphire-segmented-control__radio-input'])}
      />
      {children}
    </label>
  );
});
